/**
 * Widgets do TradingView (spec 056): calendario economico e cotacao, pela incorporacao oficial deles (o script deles monta
 * um quadro dentro da caixa). Sao gratis com a marca deles visivel: o link pro TradingView embaixo de cada um e
 * obrigatorio, nunca esconder. Seguem o tema da tela (claro ou escuro, com fundo transparente) e trocam junto com ele.
 * Se o script nao carregar (bloqueador de anuncio, rede, TradingView fora), a caixa vira um aviso com o link pro site
 * deles, em vez de ficar em branco. Nenhum sabe a marca: o app manda paises, importancia e simbolos.
 */
import { useEffect, useRef, useState } from "react";
import { temaNaTela } from "../tema";

const ORIGEM_DO_SCRIPT = "https://s3.tradingview.com/external-embedding/";
const SITE = "https://br.tradingview.com/";

export type ImportanciaDoEvento = "baixa" | "media" | "alta";
const IMPORTANCIA: Record<ImportanciaDoEvento, string> = { baixa: "-1", media: "0", alta: "1" };

/** O tema que esta na tela agora, acompanhando o botao de sol e lua e o aparelho. */
function useTemaDaTela(): "claro" | "escuro" {
  const [tema, setTema] = useState(() => (typeof document === "undefined" ? "claro" : temaNaTela()));
  useEffect(() => {
    const atualizar = () => setTema(temaNaTela());
    const observador = new MutationObserver(atualizar);
    observador.observe(document.documentElement, { attributes: true, attributeFilter: ["data-tema"] });
    const m = window.matchMedia?.("(prefers-color-scheme: dark)");
    m?.addEventListener("change", atualizar);
    return () => {
      observador.disconnect();
      m?.removeEventListener("change", atualizar);
    };
  }, []);
  return tema;
}

export type WidgetDoTradingViewProps = { rotulo: string; /** Quanto esperar o quadro aparecer antes de dar o aviso. */ limiteMs?: number } & (
  | { tipo: "calendario"; paises: string[]; importancia: ImportanciaDoEvento[]; altura?: number }
  | { tipo: "cotacao"; simbolo: string }
);

export function WidgetDoTradingView(p: WidgetDoTradingViewProps) {
  const tema = useTemaDaTela();
  const caixa = useRef<HTMLDivElement>(null);
  const [falhou, setFalhou] = useState(false);
  const comum = { colorTheme: tema === "escuro" ? "dark" : "light", isTransparent: true, width: "100%", locale: "br" };
  const config = p.tipo === "calendario" ? { ...comum, height: String(p.altura ?? 520), importanceFilter: p.importancia.map((i) => IMPORTANCIA[i]).join(","), countryFilter: p.paises.join(",") } : { ...comum, symbol: p.simbolo };
  const arquivo = p.tipo === "calendario" ? "embed-widget-events.js" : "embed-widget-single-quote.js";
  const json = JSON.stringify(config);
  const limite = p.limiteMs ?? 10_000;
  const site = p.tipo === "calendario" ? `${SITE}economic-calendar/` : `${SITE}symbols/${p.simbolo.replace(":", "-")}/`;

  // O script deles le a configuracao do proprio texto e poe o quadro na caixa onde ele esta. Trocar o tema ou o que
  // mostrar monta de novo (o widget nao troca de tema sozinho).
  useEffect(() => {
    const el = caixa.current;
    if (!el) return;
    setFalhou(false);
    const alvo = document.createElement("div");
    alvo.className = "tradingview-widget-container__widget";
    const script = document.createElement("script");
    script.src = ORIGEM_DO_SCRIPT + arquivo;
    script.async = true;
    script.textContent = json;
    script.onerror = () => setFalhou(true);
    el.replaceChildren(alvo, script);
    // Chegou atrasado (rede lenta): o quadro apareceu depois do aviso, entao o aviso sai.
    const observador = new MutationObserver(() => {
      if (el.querySelector("iframe")) setFalhou(false);
    });
    observador.observe(el, { childList: true, subtree: true });
    const espera = window.setTimeout(() => {
      if (!el.querySelector("iframe")) setFalhou(true);
    }, limite);
    return () => {
      window.clearTimeout(espera);
      observador.disconnect();
      el.replaceChildren();
    };
  }, [arquivo, json, limite]);

  return (
    <div className="mtc-tradingview" data-tipo={p.tipo}>
      {falhou && <SemTradingView rotulo={p.rotulo} site={site} />}
      <div ref={caixa} className="tradingview-widget-container" role="group" aria-label={p.rotulo} hidden={falhou} style={p.tipo === "calendario" ? { minHeight: p.altura ?? 520 } : undefined} />
      <p className="mtc-tradingview__credito">
        <a href={site} rel="noopener nofollow" target="_blank">{p.tipo === "calendario" ? "Calendário econômico" : p.rotulo} no TradingView</a>
      </p>
    </div>
  );
}

/** O widget nao carregou: diz o porque mais provavel e leva pro site deles. */
export function SemTradingView({ rotulo, site = SITE }: { rotulo: string; site?: string }) {
  return (
    <div className="mtc-tradingview__falha" role="status">
      <strong>{rotulo}: não carregou agora.</strong>
      <span>Esses dados vêm do TradingView. Se algum bloqueador de anúncio estiver ligado, libere esta página, ou veja direto no site deles.</span>
      <a className="mtc-link" href={site} rel="noopener nofollow" target="_blank">Abrir no TradingView</a>
    </div>
  );
}
